import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController, IonSelect } from '@ionic/angular';
import * as moment from 'moment';
import { LoggerService } from 'src/app/core/logger/logger.service';
import { DestinationStatus } from 'src/app/core/models/destination-status.model';
import { StatusPatchData } from 'src/app/core/models/status-patch-data.model';
import { TicketStatusTransition } from 'src/app/core/models/ticket-status-transition.model';
import { AppointmentService } from 'src/app/core/services/appointment/appointment.service';
import { TicketStatusTransitionsService } from 'src/app/core/services/ticket-status-transitions/ticket-status-transitions.service';

@Component({
	selector: 'app-change-status-button',
	templateUrl: './change-status-button.component.html',
	styleUrls: ['./change-status-button.component.scss'],
})
export class ChangeStatusButtonComponent implements OnInit, OnChanges {

	@Input()
	ticketStatusId: number;

	@Input()
	ticketStatusDesc: string;

	@Input()
	apptId: number;

	@Input()
	fgColor = 'primary';

	@Input()
	bgColor = 'primary';

	@Input()
	color = 'primary';

	@Input()
	expand = 'block';

	@Input()
	size = 'small';

	@Input()
	disabled = false;

	@Input()
	fill = 'solid';

	@ViewChild('statusSelectRef')
	statusSelectRef: IonSelect;

	transitions: TicketStatusTransition;

	constructor(
		private alertController: AlertController,
		private ticketStatusTransitionService: TicketStatusTransitionsService,
		private appointmentService: AppointmentService,
		private router: Router,
		private logger: LoggerService,
	) { }

	get validDestinations() {
		return this.transitions.destinationStatuses.filter(dest => !dest.clearCodeTabTransitionOnly);
	}

	async ngOnInit() {
		await this.getTransitions();
		this.setColors();
	}

	async ngOnChanges(changes: SimpleChanges) {
		if (!changes.ticketStatusId) {
			return;
		}

		const { currentValue, previousValue } = changes.ticketStatusId;
		if (currentValue !== previousValue) {
			await this.ngOnInit();
		}
	}

	async getTransitions() {
		this.transitions = await this.ticketStatusTransitionService.getStatusTransition(this.ticketStatusId);
	}

	async onClickShowStatusOptions(): Promise<void> {
		if (this.transitions.destinationStatuses.length == 0) {
			await this.alertDeadEnd();
			return;
		}

		await this.statusSelectRef.open();
	}

	async alertDeadEnd(): Promise<void> {
		const alert = await this.alertController.create({
			header: 'Status',
			message: 'There are no valid transitions',
			buttons: ['OK'],
		});

		await alert.present();
	}

	async onChangeUpdateStatus(event: any): Promise<void> {
		const selectedStatus = event.detail.value as DestinationStatus;
		await this.changeStatus(selectedStatus);
		if (selectedStatus.openClearCodeTab) {
			this.goToClearCodes();
		}
	}

	private async changeStatus(selectedStatus: DestinationStatus) {
		try {
			this.updateStatusAPI(selectedStatus);
			await this.updateStatus(selectedStatus);
			await this.getTransitions();
		} catch (error) {
			this.logger.logException(error);
		}
		this.setColors();
	}

	setColors(): void {
		if (this.transitions?.backgroundColor && this.transitions?.foregroundColor) {
			this.color = '#';
			this.bgColor = this.convertToColorHex(this.transitions.backgroundColor);
			this.fgColor = this.convertToColorHex(this.transitions.foregroundColor);
		}
	}

	convertToColorHex(num?: number) {
		return '#' + ((num) >>> 0).toString(16).substring(2);
	}

	goToClearCodes(): void {
		const clearCodePath = ['/appointments', this.apptId, 'clear-codes'];
		if (!this.router.url.startsWith(clearCodePath.join('/'))) {
			this.router.navigate(clearCodePath);
			return;
		}
	}

	private async updateStatus(selectedStatus: DestinationStatus): Promise<void> {
		const newStatus = await this.ticketStatusTransitionService.getStatusTransition(selectedStatus.id);
		const appointment = await this.appointmentService.getLocalAppointment(this.apptId);

		this.ticketStatusId = Number(newStatus.id);
		this.ticketStatusDesc = newStatus.ticketStateDesc;
		appointment.ticketStatusId = this.ticketStatusId;
		appointment.ticketStatusDesc = this.ticketStatusDesc;

		await this.appointmentService.saveLocalAppointment(appointment);
		this.ticketStatusTransitionService.setUpdated(appointment);
	}

	private updateStatusAPI(selectedStatus: DestinationStatus): void {
		const patchData = {
			id: this.apptId,
			prevTicketStatusId: this.ticketStatusId,
			newTicketStatusId: Number(selectedStatus.id),
			timestamp: moment().toISOString(),
		} as StatusPatchData;

		this.appointmentService.updateStatus(patchData).subscribe(
			_ => { },
			error => this.logger.logException(error),
		);
	}

}
